<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="lg"
            :no-close-on-backdrop="question_modified"
            :no-close-on-esc="question_modified"
            @cancel="cancel"
            @close="cancel"
            @hidden="hidden"
            @ok="saveQuestion"
        >
            <template #modal-title>
                <div v-if="editable_question.id">Edit Question</div>
                <div v-else>Create Question</div>
            </template>
            <div v-if="editable_question.id" class="alert alert-danger">
                <div><b>Proceed with minor edits?</b> (ex. typos)</div>
                <div>
                    <b>Major changes warrant a new question.</b>
                    (ex. meaning of question has changed or the correct answer option is wrong)
                </div>
            </div>

            <b-form-group
                v-if="!editable_question.id && !editable_question.difficulty"
                label="Question Type"
                label-for="type"
            >
                <div>
                    <div class="d-flex align-items-center">
                        <div class="flex-grow-1">
                            <multiselect
                                v-if="!this.article || (this.article && (this.article.is_activity || this.article.is_assessment))"
                                v-model="editable_question.type"
                                :options="question_types"
                                :custom-label="formattedQuestionType"
                                :class="{'is-invalid': validating && !editable_question.type}"
                                :disabled="questionHasContent || editable_question.id"
                                @input="questionTypeChanged"
                                style="z-index: 10!important"
                            >
                                <template #singleLabel="{ option }">
                                    <span class="text-capitalize">{{ formattedQuestionType(option) }}</span>
                                </template>
                                <template #option="{ option }">
                                    <span class="text-capitalize">{{ formattedQuestionType(option) }}</span>
                                </template>
                            </multiselect>
                            <multiselect
                                v-else-if="this.article && (this.article.is_rubric || this.article.is_walkthrough)"
                                v-model="editable_question.type"
                                :options="rubric_question_types"
                                :custom-label="formattedQuestionType"
                                :class="{'is-invalid': validating && !editable_question.type}"
                                :disabled="questionHasContent || editable_question.id"
                                @input="questionTypeChanged"
                                style="z-index: 10!important"
                            >
                                <template #singleLabel="{ option }">
                                    <span class="text-capitalize">{{ formattedQuestionType(option) }}</span>
                                </template>
                                <template #option="{ option }">
                                    <span class="text-capitalize">{{ formattedQuestionType(option) }}</span>
                                </template>
                            </multiselect>
                            <multiselect
                                v-else
                                v-model="editable_question.type"
                                :options="article_question_types"
                                :custom-label="formattedQuestionType"
                                :class="{'is-invalid': validating && !editable_question.type}"
                                :disabled="questionHasContent || editable_question.id"
                                @input="questionTypeChanged"
                                style="z-index: 10!important"
                            >
                                <template #singleLabel="{ option }">
                                    <span class="text-capitalize">{{ formattedQuestionType(option) }}</span>
                                </template>
                                <template #option="{ option }">
                                    <span class="text-capitalize">{{ formattedQuestionType(option) }}</span>
                                </template>
                            </multiselect>


                        </div>
                        <div v-if="questionHasContent" class="ml-3">
                            <b-btn variant="danger" @click="resetQuestion">
                                <font-awesome-icon :icon="['fad', 'trash']" />
                            </b-btn>
                        </div>
                    </div>
                </div>

                <b-form-invalid-feedback :state="validating && !editable_question.type ? false : null">
                    Question Type is required
                </b-form-invalid-feedback>
            </b-form-group>

            <div v-if="editable_question && editable_question.type">

                <div v-if="editable_question.type == 'fill_in_the_blank'">
                    <fill-in-the-blank :question="editable_question" />
                </div>

                <div v-if="editable_question.type == 'media_block'">
                    <media-block :question="editable_question" :is_walkthrough="article && article.is_walkthrough" />
                </div>
                <div v-if="editable_question.type == 'text_block'">
                    <text-block :question="editable_question" :article="article" />
                </div>

                <div v-if="editable_question.type == 'new_fill_in_the_blank'">
                    <new-fill-in-the-blank :question="editable_question" />
                </div>

                <div v-if="editable_question.type == 'labeling'">
                    <Labeling :question="editable_question" />
                </div>

                <div v-if="editable_question.type == 'matching'">
                    <Matching :question="editable_question" />
                </div>

                <div v-if="editable_question.type == 'drawing'">
                    <Drawing :question="editable_question" />
                </div>

                <div
                    v-if="editable_question.type == 'multiple_choice' || editable_question.type == 'article_assessment'"
                >
                    <multiple-choice :question="editable_question" />
                </div>

                <div v-if="editable_question.type == 'new_multiple_choice'">
                    <new-multiple-choice :question="editable_question" :article="article" />
                </div>

                <div v-if="editable_question.type == 'open_response'">
                    <open-response :question="editable_question" />
                </div>

                <div v-if="editable_question.type == 'select_all_that_apply'">
                    <select-all-that-apply :question="editable_question" />
                </div>

                <div v-if="editable_question.type == 'new_select_all_that_apply'">
                    <new-select-all-that-apply :question="editable_question" />
                </div>

                <div v-if="editable_question.type == 'sorting'">
                    <Sorting :question="editable_question" />
                </div>

                <div v-if="editable_question.type == 'true_false'">
                    <true-false :question="editable_question" />
                </div>

                <div v-if="editable_question.type == 'numeric_response'">
                    <numeric-response :question="editable_question" />
                </div>

                <div v-if="editable_question.type == 'grouping'">
                    <grouping :question="editable_question" />
                </div>

                <div v-if="editable_question.type == 'hot_spot'">
                    <hot-spot :question="editable_question" :questions="questions" />
                </div>

                <div v-if="editable_question.type == 'hot_text'">
                    <hot-text :question="editable_question" :questions="questions" />
                </div>
                <b-form-group v-if="!editable_question.difficulty" label="Standards" label-for="standards">
                    <multiselect
                        v-model="filtered_question_standards"
                        :options="available_standards"
                        :close-on-select="false"
                        :custom-label="customLabel"
                        track-by="number"
                        multiple
                    >
                        <template #tag="{ option }">
                            <span class="multiselect__tag pl-2" :content="option.description" v-tippy>
                                {{ option.number }} ({{ option.year }})
                                <i class="multiselect__tag-icon" @click="removeTag(option)"></i>
                            </span>
                        </template>
                    </multiselect>
                </b-form-group>

                <div class="mt-3 mb-3 border-top pt-3 print-view-section">
                    <div class="d-flex align-items-center justify-content-between">
                        <b-form-checkbox
                            v-model="show_print_view"
                            @change="togglePrintView"
                        >
                            Add Print View
                        </b-form-checkbox>
                         <div v-if="show_print_view" class="d-flex align-items-center">
                            <b-btn
                                variant="secondary"
                                class="mr-2"
                                content="Copy Online Question to Print View"
                                v-tippy="{placement: 'top'}"
                                :disabled="copying_online_question || !canCopyOnlineQuestionToPrintView"
                                @click="copyOnlineQuestionToPrintView"
                            >
                                <span v-if="copying_online_question">
                                    <font-awesome-icon :icon="['fas', 'check']" fixed-width />
                                </span>
                                <span v-else>
                                    <font-awesome-icon :icon="['fad', 'clone']" fixed-width />
                                </span>
                            </b-btn>
                        </div>
                    </div>

                    <b-form-group
                        v-if="show_print_view"
                        class="mt-3 mb-0"
                        label="Print View"
                        label-for="print-view"
                    >
                        <TipTap
                            :key="print_view_editor_key"
                            v-model="editable_question.print_view"
                            class="print-view-editor"
                            :article="article"
                            :menu_items="['bold', 'italic', 'underline', 'bullet_list', 'ordered_list', 'image', 'edit_html']"
                            :exclude_save="true"
                            :exclude_copy="true"
                            @input="setPrintView"
                        />
                    </b-form-group>
                </div>
            </div>

            <template #modal-footer>
                <b-button variant="light" @click="cancel">
                    Cancel
                </b-button>
                <b-button :variant="editable_question.id ? 'primary' : 'success'" @click="saveQuestion">
                    <div v-if="editable_question.id">
                        <font-awesome-icon :icon="['fad', 'check-square']" fixed-width />
                        Save Changes
                    </div>
                    <div v-else>
                        <font-awesome-icon :icon="['fad', 'plus-square']" fixed-width />
                        Create Question
                    </div>
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>
.print-view-section {
    background-color: #f3f3f3;
    border-radius: 0.5rem;
    padding: 1rem;
}

.print-view-editor::v-deep .tiptap {
    background-color: #f3f3f3;
    border-radius: 0.5rem;
}
</style>

<script>
import {mapState} from 'vuex'

import question_types from '@/common/question_types'
import article_question_types from '@/common/article_question_types'
import rubric_question_types from '@/common/rubric_question_types'

import FillInTheBlank from '@/components/modals/questions/content/FillInTheBlank'
import NewFillInTheBlank from '@/components/modals/questions/content/NewFillInTheBlank'
import Labeling from '@/components/modals/questions/content/Labeling'
import Matching from '@/components/modals/questions/content/Matching'
import MultipleChoice from '@/components/modals/questions/content/MultipleChoice'
import NewMultipleChoice from '@/components/modals/questions/content/NewMultipleChoice'
import OpenResponse from '@/components/modals/questions/content/OpenResponse'
import TextBlock from '@/components/modals/questions/content/TextBlock'
import MediaBlock from '@/components/modals/questions/content/MediaBlock'
import SelectAllThatApply from '@/components/modals/questions/content/SelectAllThatApply'
import NewSelectAllThatApply from '@/components/modals/questions/content/NewSelectAllThatApply'
import Sorting from '@/components/modals/questions/content/Sorting'
import TrueFalse from '@/components/modals/questions/content/TrueFalse'
import NumericResponse from '@/components/modals/questions/content/NumericResponse'
import Grouping from '@/components/modals/questions/content/Grouping'
import HotSpot from '@/components/modals/questions/content/HotSpot'
import HotText from '@/components/modals/questions/content/HotText'
import Drawing from '@/components/modals/questions/content/Drawing'
import TipTap from '@/components/tiptap/TipTap'
import {formattedQuestionType} from '@/common/formattedQuestionType'
import {buildQuestionPrintView} from '@/common/buildQuestionPrintView'

export default {
    data: () => ({
        question_types,
        article_question_types,
        rubric_question_types,
        editable_question: {},
        editable_question_cache: {},
        walkthrough_q_types: [
            'text_block',
            'media_block'
        ],
        filtered_question_standards: [],
        show_print_view: false,
        copying_online_question: false,
        print_view_editor_key: 0,
    }),
    props: [
        'value',
        'unit_group',
        'unit',
        'question',
        'questions',
        'article',
        'state_grade',
        'available_standards',
    ],
    components: {
        FillInTheBlank,
        NewFillInTheBlank,
        Labeling,
        Matching,
        MultipleChoice,
        NewMultipleChoice,
        OpenResponse,
        SelectAllThatApply,
        NewSelectAllThatApply,
        Sorting,
        TrueFalse,
        NumericResponse,
        Grouping,
        TextBlock,
        MediaBlock,
        HotSpot,
        HotText,
        Drawing,
        TipTap
    },
    computed: {
        ...mapState({
            standards: state => state.standards.standards,
            validating: state => state.utilities.validating
        }),
        modal_id() {
            let modal_id = 'question-modal'
            return modal_id
        },
        question_modified() {
            let question_modified = false

            if (
                // !_.isEqual(this.editable_question.content, this.editable_question_cache.content) ||
                // !_.isEqual(this.editable_question.standards, this.editable_question_cache.standards)
                !_.isEqual(this.editable_question, this.editable_question_cache)
            ) {
                question_modified = true
            }

            return question_modified
        },
        questionHasContent() {
            let questionHasContent = false
            if (_.some(this.editable_question.content)) {
                questionHasContent = true
            }
            return questionHasContent
        },
        standards() {
            let standards = []
            if (this.unit_group && this.unit_group.standards) {
                standards = this.unit_group.standards
            } else if (this.unit && this.unit.standards) {
                standards = this.unit.standards
            }
            return standards
        },
        canCopyOnlineQuestionToPrintView() {
            return !!buildQuestionPrintView(this.editable_question)
        }
    },
    watch: {
        value: {
            handler(value) {
                if (value == true) {
                    this.$store.commit('setValidating', false)

                    if (this.question) {
                        if (this.question.premodified) {
                            delete this.question.premodified
                            this.editable_question = _.cloneDeep(this.question)
                            this.editable_question_cache = _.cloneDeep(this.question)
                            this.editable_question_cache.content = ""
                            this.filterQuestionStandards();
                        } else {
                            this.editable_question = _.cloneDeep(this.question)
                            this.editable_question_cache = _.cloneDeep(this.question)
                            this.filterQuestionStandards();
                        }

                        this.show_print_view = !!this.editable_question.print_view
                    } else {
                        this.show_print_view = !!this.editable_question.print_view
                    }
                }
            }
        }
    },
    methods: {
        formattedQuestionType,
        customLabel(option) {
            return `${option.number} ( ${option.year} ) - ${option.description}`;
        },
        removeTag(option) {
            this.filtered_question_standards = this.filtered_question_standards.filter(
                (item) => item.number !== option.number
            );
        },
        saveQuestion() {
            this.$store.commit('setValidating', true)
            if (!this.validatingForm()) {
                return
            }

            this.$set(this.editable_question, 'standards', this.filtered_question_standards);

            if (this.question_modified) {
                if (this.editable_question.id) {
                    this.$swal({
                        html: '<div>Proceed with minor edits?</div> <div style="color:red;">Major Changes warrent a new question.</div>',
                        showCancelButton: true,
                        confirmButtonText: 'Update Question',
                        cancelButtonText: 'Cancel',
                        reverseButtons: true
                    }).then((result) => {
                        if (result.value) {
                            this.$emit('update', this.editable_question)
                        }
                    })
                } else {
                    if (this.article && this.article.is_walkthrough) {
                        this.$emit('createWalkthroughQuestion', this.editable_question)
                    } else {
                        this.$emit('create', this.editable_question)
                    }
                }
            } else {
                this.hide()
            }
        },
        validatingForm() {
            let valid = true

            if (this.editable_question.content) {
                if (this.editable_question.type == 'fill_in_the_blank' || this.editable_question.type == 'new_fill_in_the_blank') {
                    if (!this.editable_question.content.question) {
                        valid = false
                    }
                    if (this.editable_question.content.blanks && this.editable_question.content.blanks.length) {
                        _.forEach(this.editable_question.content.blanks, blank => {
                            if (!blank.is_open_response) {
                                if (!blank.answer) {
                                    valid = false
                                }
                                if (blank.options && blank.options.length) {
                                    _.forEach(blank.options, option => {
                                        if (!option.option) {
                                            valid = false
                                        }
                                    })
                                }
                            }
                        })
                    } else {
                        valid = false
                    }
                }

                if (this.editable_question.type == 'labeling') {
                    if (!this.editable_question.content.question) {
                        valid = false
                    } else if (!this.editable_question.content.image) {
                        valid = false
                    } else if (
                        !this.editable_question.content.labels ||
                        (this.editable_question.content.labels && !this.editable_question.content.labels.length)
                    ) {
                        valid = false
                    }

                    _.forEach(this.editable_question.content.labels, label => {
                        if (!label.text && !label.url) {
                            valid = false
                        }
                    })

                    if (this.editable_question.content.incorrect_clues && this.editable_question.content.incorrect_clues.length) {
                        _.forEach(this.editable_question.content.incorrect_clues, incorrect_clue => {
                            if (!incorrect_clue.text && !incorrect_clue.url) {
                                valid = false
                            }
                        })
                    }
                }

                if (this.editable_question.type == 'matching') {
                    if (!this.editable_question.content.question_prompt) {
                        valid = false
                    } else if (
                        !this.editable_question.content.answer_pairs ||
                        (this.editable_question.content.answer_pairs && !this.editable_question.content.answer_pairs.length)
                    ) {
                        valid = false
                    }

                    _.forEach(this.editable_question.content.answer_pairs, answer_pair => {
                        if (answer_pair.left.type == 'text' && !answer_pair.left.text) {
                            valid = false
                        } else if (answer_pair.left.type == 'image' && !answer_pair.left.image) {
                            valid = false
                        } else if (answer_pair.right.type == 'text' && !answer_pair.right.text) {
                            valid = false
                        } else if (answer_pair.right.type == 'image' && !answer_pair.right.image) {
                            valid = false
                        }
                    })
                }

                if (this.editable_question.type == 'multiple_choice') {
                    if (!this.editable_question.content.question) {
                        valid = false
                    } else if (this.editable_question.content.answer == null) {
                        valid = false
                    }

                    //allow less than 3 answers
                    _.remove(this.editable_question.content.possible_answers, answer => {
                        return !answer.value
                    })
                }

                if (this.editable_question.type == 'new_multiple_choice') {
                    if (!this.editable_question.content.question) valid = false
                    if (!this.editable_question.content.answer) valid = false

                    _.forEach(this.editable_question.content.possible_answers, possible_answer => {
                        if (!possible_answer.value) valid = false
                    })

                    if (valid) {
                        _.each(this.editable_question.content.possible_answers, answer => {
                            delete answer.id
                            delete answer.show_image_modal
                        })
                    }

                    return valid
                }

                if (this.editable_question.type == 'open_response') {
                    if (!this.editable_question.content.question) {
                        valid = false
                    }
                }

                if (this.editable_question.type == 'select_all_that_apply') {
                    if (!this.editable_question.content.question) {
                        valid = false
                    } else if (
                        !this.editable_question.content.answers ||
                        this.editable_question.content.answers && !this.editable_question.content.answers.length
                    ) {
                        valid = false
                    }

                    _.forEach(this.editable_question.content.possible_answers, possible_answer => {
                        if (!possible_answer.value) {
                            valid = false
                        }
                    })
                }

                if (this.editable_question.type == 'new_select_all_that_apply') {
                    if (!this.editable_question.content.question) valid = false
                    if (!this.editable_question.content.answers.length) valid = false

                    _.forEach(this.editable_question.content.possible_answers, possible_answer => {
                        if (!possible_answer.value) valid = false
                    })

                    if (valid) {
                        _.each(this.editable_question.content.possible_answers, answer => {
                            delete answer.id
                            delete answer.show_image_modal
                        })
                    }

                    return valid
                }

                if (this.editable_question.type == 'sorting') {
                    if (!this.editable_question.content.question) {
                        valid = false
                    } else if (!this.editable_question.content.direction) {
                        valid = false
                    } else if (
                        !this.editable_question.content.options ||
                        this.editable_question.content.options && this.editable_question.content.options.length < 2
                    ) {
                        valid = false
                    }

                    _.forEach(this.editable_question.content.options, option => {
                        if (option.type == 'text' && !option.text) {
                            valid = false
                        } else if (option.type == 'image' && !option.image) {
                            valid = false
                        }
                    })
                }

                if (this.editable_question.type == 'true_false') {
                    if (!this.editable_question.content.question) {
                        valid = false
                    } else if (!this.editable_question.content.answer) {
                        valid = false
                    }
                }

                if (this.editable_question.type == 'numeric_response') {
                    if (!this.editable_question.content.question) {
                        valid = false
                    } else if (!this.editable_question.content.answer) {
                        valid = false
                    }
                }

                if (this.editable_question.type == 'grouping') {
                    if (!this.editable_question.content.question) valid = false
                    if (!this.editable_question.content.items.length) valid = false

                    _.each(this.editable_question.content.items, item => {
                        if (!item.value) valid = false
                        if (!item.region) valid = false
                    })

                    _.each(this.editable_question.content.regions, region => {
                        if (!region.title) valid = false
                    })

                    if (valid) {
                        _.each(this.editable_question.content.items, item => {
                            delete item.id
                            delete item.show_image_modal
                        })
                    }

                    return valid
                }

                if (this.editable_question.type === 'hot_spot') {
                    const clues = this.editable_question.content.clues ? this.editable_question.content.clues : []
                    if (clues.length === 0) {
                        valid = false
                    } else {
                        clues.forEach((clue) => {
                            if (clue.type === 'text' && !clue.is_empty && (!clue.text || clue.text.length === 0)) {
                                valid = false
                            }
                        })
                    }
                }

                if (this.editable_question.type === 'hot_text') {
                    //confirm question exists
                    if (!this.editable_question.content.question || this.editable_question.content.question == '') {
                        valid = false;
                    }
                    //confirm instruction exists
                    if (!this.editable_question.content.instruction || this.editable_question.content.instruction == '') {
                        valid = false;
                    }
                    //confirm that at least 2 options and at least one is correct
                    const number_of_targets = (this.editable_question.content.question.match(/<hot_text/g) || []).length;
                    if (number_of_targets < 2) {
                        valid = false;
                    }
                    //confirm that at least 1 target is correct
                    const number_of_correct_targets = (this.editable_question.content.question.match(/hot_text_correct/g) || []).length;
                    if (number_of_correct_targets < 1) {
                        valid = false;
                    }
                }
            } else {
                valid = false
            }

            return valid
        },
        resetQuestion() {
            this.$swal({
                html: 'Are you sure you want to discard this question? If you proceed, all changes will be lost.',
                showCancelButton: true,
                confirmButtonText: 'Discard Question',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.$store.commit('setValidating', false)

                    this.editable_question = {}
                    this.editable_question_cache = {}
                    this.show_print_view = false
                }
            })
        },
        togglePrintView(enabled) {
            if (!enabled) {
                this.$set(this.editable_question, 'print_view', null)
                return
            }

            if (!this.editable_question.print_view) {
                this.$set(this.editable_question, 'print_view', '')
            }
        },
        setPrintView(value) {
            this.$set(this.editable_question, 'print_view', value)
        },
        async copyOnlineQuestionToPrintView() {
            if (!this.canCopyOnlineQuestionToPrintView) {
                return
            }

            const result = await this.$swal({
                html: 'Copy the current online question into the print view? This will overwrite the current print view content.',
                showCancelButton: true,
                confirmButtonText: 'Copy to Print View',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })

            if (!result.value) {
                return
            }

            this.$set(this.editable_question, 'print_view', buildQuestionPrintView(this.editable_question))
            this.show_print_view = true
            this.print_view_editor_key += 1
            this.copying_online_question = true

            setTimeout(() => {
                this.copying_online_question = false
            }, 1500)
        },
        questionTypeChanged() {
            this.$store.commit('setValidating', false)
            this.filterQuestionStandards();
        },
        cancel(event) {
            event.preventDefault()

            if (this.question_modified) {
                this.$swal({
                    html: 'Changes have been made to this question. If you proceed, all changes will be lost.',
                    showCancelButton: true,
                    confirmButtonText: 'Discard Changes',
                    cancelButtonText: 'Keep Editing',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        if (this.question) {
                            this.$set(this.question, 'content', this.editable_question_cache.content)
                        }

                        this.hide()
                    }
                })
            } else {
                this.hide()
            }
        },
        hide() {
            this.$emit('hide')
        },
        hidden() {
            this.editable_question = {}
            this.editable_question_cache = {}
            this.show_print_view = false
            this.copying_online_question = false
            this.print_view_editor_key = 0

            this.$emit('hide')
        },
        filterQuestionStandards() {
            this.filtered_question_standards = [];
            _.each(this.editable_question.standards, standard => {
                if (
                    this.state_grade
                    &&
                    this.state_grade.standards_source != 'none'
                    &&
                    (
                        (this.state_grade.standards_source == 'state' && this.state_grade.grade >= standard.low_grade && this.state_grade.grade <= standard.high_grade && this.state_grade.state == standard.state)
                        ||
                        (this.state_grade.standards_source == 'national' && this.state_grade.grade >= standard.low_grade && this.state_grade.grade <= standard.high_grade && standard.state == 'NA')
                    )
                ) {
                    this.filtered_question_standards.push(standard)
                }
                if (!this.state_grade) this.filtered_question_standards.push(standard)
            });
        }
    }
}
</script>
